// Reads master first: `ValidateSequenceOnRoute` 400s the whole call on any slot the partition lacks.
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { collectMasterRouteSlotIds, toMasterSlotSequenceEntries } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { SlotSequence } from 'types/api/route/routeSlotRequests'
import type { DayOfWeek, RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface SequenceMasterSlotStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly orderedRows: ReadonlyArray<SlotRow>
  readonly futureStops?: boolean
}

export type SequenceMasterSlotStepValue = {
  readonly committedSequence: ReadonlyArray<SlotSequence>
  readonly skippedNotOnMaster: number
}

export type SequenceMasterSlotStepResult = StepResult<'RESEQUENCE_MASTER', SequenceMasterSlotStepValue>

export const makeSequenceMasterSlotStep = (
  args: SequenceMasterSlotStepArgs,
  deps: Deps
): PipelineStep<'RESEQUENCE_MASTER', SequenceMasterSlotStepResult> => {
  const stepId = `sequence-master-slot:${args.routeId}:${args.dayOfWeek}`
  return {
    id: stepId,
    phase: 'RESEQUENCE_MASTER',
    label: 'Failed to resequence the master sequence',
    applyOrder: 300,
    run: async ctx => {
      if (!deps.sequenceMasterSlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'sequenceMasterSlot dep not provided — the v3-slot strategy requires it' })
      }

      let masterRouteSlotIds: ReadonlySet<string>
      try {
        const masterStops = await deps.getMasterStops({ routeId: args.routeId, dayOfWeek: args.dayOfWeek }).unwrap()
        masterRouteSlotIds = collectMasterRouteSlotIds(masterStops)
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'RESEQUENCE_MASTER', status, message })
      }

      const sequence = toMasterSlotSequenceEntries(args.orderedRows, masterRouteSlotIds)
      const skippedNotOnMaster = args.orderedRows.length - sequence.length
      if (sequence.length === 0) {
        ctx.logger.debug(`${stepId}: no submitted row is on master, skipping`)
        return ok({ phase: 'RESEQUENCE_MASTER', stepId, value: { committedSequence: [], skippedNotOnMaster } })
      }

      try {
        const response = await deps
          .sequenceMasterSlot({
            routeId: args.routeId,
            data: { dayOfWeek: args.dayOfWeek as ApiDayOfWeek, sequence: [...sequence], futureStops: args.futureStops ?? true },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({
          phase: 'RESEQUENCE_MASTER',
          stepId,
          value: { committedSequence: response?.sequence ?? sequence, skippedNotOnMaster },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'RESEQUENCE_MASTER', status, message })
      }
    },
  }
}
